/**
 * useSignatures Hook
 *
 * CRUD for email signatures + get default signature via React Query.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import {
  api,
  EmailSignature,
  EmailCreateSignatureInput,
  EmailUpdateSignatureInput,
} from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { errorMessage } from '../types';

export function useSignatures(accountId: string | undefined) {
  const queryClient = useQueryClient();

  const queryResult = useQuery({
    queryKey: queryKeys.email.signatures(accountId ?? ''),
    queryFn: async () => {
      if (!accountId) throw new Error('No account selected');
      return api.listEmailSignatures(accountId);
    },
    enabled: !!accountId,
  });

  const signatures = queryResult.data?.signatures ?? ([] as EmailSignature[]);
  const defaultSignature = useMemo(() => signatures.find((s) => s.isDefault) ?? null, [signatures]);

  const invalidate = useCallback(() => {
    if (accountId) {
      queryClient.invalidateQueries({ queryKey: queryKeys.email.signatures(accountId) });
    }
  }, [accountId, queryClient]);

  const createSignature = useCallback(
    async (input: EmailCreateSignatureInput) => {
      const result = await api.createEmailSignature(input);
      invalidate();
      return result;
    },
    [invalidate],
  );

  const updateSignature = useCallback(
    async (signatureId: string, input: EmailUpdateSignatureInput) => {
      if (!accountId) return;
      const result = await api.updateEmailSignature(signatureId, accountId, input);
      invalidate();
      return result;
    },
    [accountId, invalidate],
  );

  const deleteSignature = useCallback(
    async (signatureId: string) => {
      if (!accountId) return;
      await api.deleteEmailSignature(signatureId, accountId);
      invalidate();
    },
    [accountId, invalidate],
  );

  return {
    signatures,
    defaultSignature,
    loading: queryResult.isLoading,
    error: errorMessage(queryResult.error),
    refresh: queryResult.refetch,
    createSignature,
    updateSignature,
    deleteSignature,
  };
}
